import moment from "moment";

import { AddContactPointWarningPopup } from "@/Appointments/Components/Calendar/AddContactPointWarningPopup";
import { AddRepresentativeEmailPopup, AddRepresentativeEmailPopupProps } from "@/Appointments/Components/Calendar/AddRepresentativeEmailPopup";
import { PredefinedAppointmentItem } from "@/Appointments/Models/Scheduling/PredefinedAppointmentItem";
import { INotificationContactPoint } from "@/Common/Api/Contacts/Dto/INotificationContactPoint";
import { Employee } from "@/Common/Api/Employee/Dto/Employee";
import { FlatLookupView } from "@/Common/Api/MedfarDictionaries/Dto/Responses/FlatLookupView";
import { BaseReactComponent } from "@/Common/Components/BaseReactComponent";
import { FeatureFlagControl } from "@/Common/Components/FeatureFlagControl";
import { CheckboxControl } from "@/Common/Components/Inputs/CheckboxControl";
import { TextAreaResizable } from "@/Common/Components/Inputs/TextAreaResizable";
import { TextboxControl } from "@/Common/Components/Inputs/TextboxControl";
import { PatientContactDisplay } from "@/Common/Components/Patient/PatientContactDisplay";
import { DoctorSearchAutocomplete } from "@/Common/Components/Search/DoctorSearchAutocomplete";
import { PatientEmailValidator } from "@/Common/Helpers/PatientEmailValidator";
import { PatientHelper } from "@/Common/Helpers/PatientHelper";
import { Guid } from "@/Common/Models/Guid";
import { IReferralDoctorModel } from "@/Common/Models/IReferralDoctorModel";
import { QuestionnaireStatusInfo } from "@/Common/Models/Questionnaires/QuestionnaireStatusInfo";
import { SearchableItemModelT } from "@/Common/Models/SearchableItem";
import { ServiceDefinitionModel } from "@/Common/Models/Services/ServiceDefinitionModel";
import { TimeSpan } from "@/Common/Models/TimeSpan";
import * as LocalOperations from "@/Common/Operations/operations.local";
import * as LocalPatientOperations from "@/Common/Operations/operations.patient.local";
import { LocalEvent } from "@/Common/Stores/Actions/LocalEvent";
import { DirectoryEntityType } from "@/ContactDirectory/Api/medfar-contactdirectory/dist";
import { FaxDirectorySearchComponent } from "@/ContactDirectory/Components/Search/FaxDirectorySearchComponent";
import { DirectoryHealthCareProvider } from "@/ContactDirectory/Models/DirectoryHealthCareProvider";

interface IPredefinedAppointmentSave {
    model?: PredefinedAppointmentSaveState;
    patientContactId: Guid;
    customDuration: number;
}

class PredefinedAppointmentSaveState {
    // DTO
    public predefinedAppointmentType: PredefinedAppointmentType;
    public selectedReferalDoctor: IReferralDoctorModel;
    public referalPhoneNumber: string;
    public selectedAppointmentStateCode: string;
    public notes: string;
    public predefinedAppointments: PredefinedAppointmentItem[];
    public encryptedSaveKey: string;
    public notifyPatient: boolean;
    public notifyPatientDisabled: boolean;
    public isTeleconsultation: boolean;
    public showAddEmailPopup: boolean = false;
    public addEmailPopupProps: AddRepresentativeEmailPopupProps;
    public contactPointNotificationIds: string[];
    public sendToAllPersonalContact: boolean = false;

    // CachedLists
    public appointmentStates: FlatLookupView[];

    // Search String
    public searchReferralDoctorString: string;

    public isSaveDisabled: boolean;
    public showAddContactPointWarning: boolean = false;

    public constructor(init?: Partial<PredefinedAppointmentSaveState>) {
        Object.assign(this, init);
    }

    public static default(): PredefinedAppointmentSaveState {
        return new PredefinedAppointmentSaveState({
            encryptedSaveKey: null,
            selectedReferalDoctor: null,
            referalPhoneNumber: "",
            selectedAppointmentStateCode: "UNCONFIRMED",
            notes: "",
            predefinedAppointments: [],
            appointmentStates: cacheStore.FindLookupsByTypeCode("SITEMGMT.APPOINTMENTS.STATE"),
            searchReferralDoctorString: ""
        });
    }
}

enum PredefinedAppointmentType {
    PredefinedAppointment,
    OptimizedResult,
    OptimizedResultAny
}

class PredefinedAppointmentSave extends BaseReactComponent<IPredefinedAppointmentSave, PredefinedAppointmentSaveState> {
    private miscDispatcherReference;

    public componentWillMount(): void {
        if (this.props.model != null)
            this.setState(this.props.model);

        this.miscDispatcherReference = MiscDispatcher.register(payload => this.onDispatchEvent(payload));
    }

    public componentDidMount(): void {
        $(window).trigger("resize");
        this.refreshAppointmentOptions();
    }

    public componentWillUnmount(): void {
        super.componentWillUnmount();
        MiscDispatcher.unregister(this.miscDispatcherReference);
        this.protectState();
    }

    public componentWillUpdate(nextProps: IPredefinedAppointmentSave): void {
        if (nextProps.patientContactId != this.props.patientContactId) {
            this.protectState();
            this.setState(nextProps.model);
        }
    }

    private AutoCompleteSelectCallBack(code: string, sdModel: ServiceDefinitionModel, predefinedAppointmentInfo: PredefinedAppointmentItem): void {
        this.mutatePredefinedAppointmentList(
            predefinedAppointmentInfo.key,
            s => {
                s.DefaultDuration = sdModel.Model.DefaultDuration;
                s.Duration = sdModel.Duration;
                s.Name = cacheStore.GetLocalizedValueByLookupId(sdModel.Model.NameLookupId);
                s.ServiceDefinitionId = sdModel.Model.Id;
                s.IsNotifyPatientDisabled = sdModel.Model.IsNotifyPatientDisabledByDefault;
                s.IsTeleconsultation = sdModel.Model.IsTeleconsultation;
                s.Subject = ""; // reset search text when a value is chosen
                s.HasQuestionnaire = !!sdModel.GetQuestionnaireId();
                s.QuestionnaireId = sdModel.GetQuestionnaireId();
                if (this.state.predefinedAppointmentType === PredefinedAppointmentType.OptimizedResultAny)
                    this.changeItem(s);
            },
            () => this.refreshAppointmentOptions()
        );
    }

    public render(): JSX.Element {
        let appointmentRender = null;
        let employeeList: Employee[] = [];
        let serviceList: ServiceDefinitionModel[] = [];

        this.fillEmployeesAndServicesList(this.state.predefinedAppointments, employeeList, serviceList);
        employeeList = employeeList.distinct();

        if (this.state.predefinedAppointments && this.state.predefinedAppointments.length > 0) {
            appointmentRender = this.state.predefinedAppointments.map((s: PredefinedAppointmentItem, idx: number) => {
                const predefinedAppointmentInfo = s;

                const isModifyEnabled = this.state.predefinedAppointmentType != PredefinedAppointmentType.OptimizedResult
                    && this.state.predefinedAppointmentType != PredefinedAppointmentType.OptimizedResultAny;

                const canChangeSubject = (this.state.predefinedAppointmentType != PredefinedAppointmentType.OptimizedResult
                    && this.state.predefinedAppointmentType != PredefinedAppointmentType.OptimizedResultAny)
                    || idx == 0;

                const IsEmployeeInEditMode = predefinedAppointmentInfo.IsInEditMode && employeeList.length > 1;

                const isValid = predefinedAppointmentInfo.ServiceDefinitionId != null;

                const endDate = this.state.predefinedAppointmentType === PredefinedAppointmentType.PredefinedAppointment
                    ? predefinedAppointmentInfo.GetEnd()
                    : moment(predefinedAppointmentInfo.Start).toDate();

                if (this.state.predefinedAppointmentType === PredefinedAppointmentType.OptimizedResultAny) {
                    endDate.setMinutes(endDate.getMinutes() + this.props.customDuration);
                }
                else if (this.state.predefinedAppointmentType === PredefinedAppointmentType.OptimizedResult) {
                    endDate.setMinutes(endDate.getMinutes() + TimeSpan.FromTicks(s.Duration).TotalMinutes());
                }

                // No Autocomplete for Optimized results
                const AutoCompleteSubjects = (this.state.predefinedAppointmentType !== PredefinedAppointmentType.OptimizedResult) ?
                    serviceList.map(x => new SearchableItemModelT(x.Name, x.Model.Id, x)).toImmutable()
                    : [].toImmutable();

                return (
                    <div key={"__a" + idx}>
                        <div className="appsearch-result-container">
                            <div className="appsearch-result-item">
                                <div className="appsearch-result-item-label" style={{ flexDirection: "column", justifyContent: "center", display: "flex", paddingTop: "10px" }}>
                                    <div>{cacheStore.GetString("SubjectLabel")}</div>
                                </div>
                                {canChangeSubject && (
                                    <div className="appsearch-result-item-content-subject" style={{ width: "100%" }}>
                                        <TextboxControl
                                            onMountFocus
                                            value={predefinedAppointmentInfo == null ? "" : predefinedAppointmentInfo.Subject}
                                            autoCompleteItems={AutoCompleteSubjects}
                                            onChange={(val: string) => {
                                                this.mutatePredefinedAppointmentList(predefinedAppointmentInfo.key, s => {
                                                    s.Subject = val;
                                                    if (this.state.predefinedAppointmentType === PredefinedAppointmentType.OptimizedResultAny)
                                                        this.changeItem(s);
                                                }, () => this.refreshNotifyPatientStatus());
                                            }}
                                            onAutoCompleteSelectCallBack={(code, model) => this.AutoCompleteSelectCallBack(code, model, predefinedAppointmentInfo)}
                                            placeholder=""
                                        />
                                    </div>
                                )}
                                {!canChangeSubject && (
                                    <div className="appsearch-result-item-content" style={{ paddingTop: "9px" }}>
                                        <span>
                                            {this.state.predefinedAppointments[0].Subject}
                                        </span>
                                    </div>
                                )}
                            </div>
                            <div className="appsearch-result-item">
                                <div className="appsearch-result-item-label" />
                                <div className="appsearch-result-item-content">
                                    <span style={{ fontStyle: "italic" }}> <b> {cacheStore.GetString("ServiceLabel")}: </b> {predefinedAppointmentInfo.Name} </span>
                                </div>
                            </div>
                            {!predefinedAppointmentInfo.IsInEditMode && (
                                <div className="appsearch-result-item" style={{ justifyContent: "flex-start" }}>
                                    <div className="appsearch-result-item-label">
                                        <span>{cacheStore.GetString("DateLabel")}</span>
                                    </div>
                                    <div className="appsearch-result-item-content">
                                        <span>{moment(predefinedAppointmentInfo.Start).format("YYYY/MM/DD") + ": "}</span>
                                        <strong>{moment(predefinedAppointmentInfo.Start).format("HH:mm") + " - " + moment(endDate).format("HH:mm")}</strong>
                                        {isModifyEnabled
                                            && !predefinedAppointmentInfo.IsInEditMode
                                            && (
                                                <div className="appsearch-result-item-content-modify-action">
                                                    <div className="pull-right">
                                                        <a
                                                            onClick={() =>
                                                                this.mutatePredefinedAppointmentList(predefinedAppointmentInfo.key, s => {
                                                                    s.IsInEditMode = true;
                                                                })
                                                            }
                                                        >
                                                            {cacheStore.GetString("ModifyLabel")}
                                                        </a>
                                                    </div>
                                                </div>
                                            )
                                        }
                                    </div>
                                </div>
                            )}
                            {/* Cant Change Duration for OptimizedSearch */}
                            {this.state.predefinedAppointmentType !== PredefinedAppointmentType.OptimizedResultAny && predefinedAppointmentInfo.IsInEditMode && (
                                <div className="appsearch-result-item">
                                    <div className="appsearch-result-item-label" style={{ paddingTop: "5px" }}>
                                        <span>{cacheStore.GetString("DurationLabel")}</span>
                                    </div>
                                    <div className="flex-container">
                                        <TextboxControl
                                            IsNumeric
                                            value={(
                                                this.props.customDuration
                                                || ((predefinedAppointmentInfo.Duration || predefinedAppointmentInfo.DefaultDuration) / 600000000)
                                            ).toString()}
                                            className="form-control"
                                            onChange={(value: number) =>
                                                this.mutatePredefinedAppointmentList(
                                                    predefinedAppointmentInfo.key,
                                                    s => s.Duration = value * 600000000
                                                )
                                            }
                                        />
                                        <div style={{ paddingTop: "5px", paddingLeft: "2px" }}>
                                            <span> min</span>
                                        </div>
                                    </div>
                                    {predefinedAppointmentInfo.IsInEditMode === true && (
                                        <div className="flex-container appsearch-result-item-content-modify-action">
                                            {isValid == true && (
                                                <a
                                                    onClick={() => this.clearItem(predefinedAppointmentInfo)}
                                                    style={{ marginRight: "5px" }}
                                                >
                                                    {cacheStore.GetString("CancelLabel")}
                                                </a>
                                            )}
                                            <a style={{ marginRight: "5px" }}> | </a>
                                            <a onClick={() => this.changeItem(predefinedAppointmentInfo)}>OK</a>
                                        </div>
                                    )}
                                </div>
                            )}

                            <div className="appsearch-result-item">
                                <div className="appsearch-result-item-label">
                                    <span>{cacheStore.GetString("StaffLabel")}</span>
                                </div>
                                <div className="appsearch-result-item-content">
                                    {IsEmployeeInEditMode == true && (
                                        <select className="form-control myle-control" value={predefinedAppointmentInfo.EmployeeId}>
                                            {employeeList.map((employee: Employee) => (
                                                <option key={employee.ContactFacet_id} value={employee.ContactFacet_id}>{cacheStore.GetCliniciansPersonsByContactFacetId(employee.ContactFacet_id).NameWithPrefix}</option>)
                                            )}
                                        </select>
                                    )}

                                    {IsEmployeeInEditMode != true &&
                                        <div style={{ marginRight: "10px" }}>{cacheStore.GetCliniciansPersonsByContactFacetId(predefinedAppointmentInfo.EmployeeId).NameWithPrefix}</div>
                                    }
                                </div>
                            </div>
                        </div>
                    </div>
                );
            });
        }

        return (
            <div className="fxheight">
                {this.state.showAddEmailPopup &&
                    <AddRepresentativeEmailPopup {...this.state.addEmailPopupProps} />
                }
                {this.state.showAddContactPointWarning && (
                    <AddContactPointWarningPopup
                        patient={patientStore.getPersonModel(this.props.patientContactId)}
                        onClose={() => this.mutateState(s => {
                            s.showAddContactPointWarning = false;
                            s.isSaveDisabled = false;
                        })}
                        onContinue={() => this.mutateState(s => s.showAddContactPointWarning = false, () => this.saveAppointment())}
                        onContinueWithoutContact={() => this.mutateState(s => s.showAddContactPointWarning = false, () => this.saveAppointment())}
                    />
                )}
                <div className="scrollable" data-hvar="80">
                    <div className="flex-container patient-appointment">
                        <div className="flx-col left">
                            {appointmentRender}
                            <div>
                                <div className="grayunderline patient">
                                    <span className="graytext">{cacheStore.GetString("DetailsLabel")}</span>
                                </div>
                                <div className="flex-container" style={{ justifyContent: "flex-start", marginTop: "10px" }}>
                                    <div>
                                        {(cacheStore.ClinicSettings_Portal.EnablePortalNotifications) && (
                                            <CheckboxControl
                                                disabled={this.state.notifyPatientDisabled}
                                                checked={this.state.notifyPatient}
                                                label={cacheStore.GetString("NotifyPatientLabel")}
                                                onChange={checked => this.onNotifyPatientCheck(checked)}
                                            />
                                        )}
                                    </div>
                                    <div style={{ marginLeft: "10px" }}>
                                        {cacheStore.ClinicSettings.EnableIsTeleconsultationCheckbox && (
                                            <CheckboxControl
                                                checked={this.state.isTeleconsultation}
                                                label={cacheStore.GetString("TeleconsultationLabel")}
                                                onChange={checked => this.mutateState(s => s.isTeleconsultation = checked, this.refreshNotifyPatientStatus)}
                                            />
                                        )}
                                    </div>
                                </div>
                                {this.hasQuestionnaire && (
                                    <div>
                                        <span>{`${cacheStore.GetString("QuestionnaireLabel")}:`}</span>
                                        <span style={{ marginLeft: "10px" }}>
                                            {this.state.predefinedAppointments
                                                .filter(p => p.HasQuestionnaire)
                                                .map(p => p.QuestionnaireId)
                                                .distinct()
                                                .map(id => new QuestionnaireStatusInfo({ QuestionnaireId: id }).renderLink())
                                            }
                                        </span>
                                    </div>
                                )}
                            </div>
                        </div>
                        <div className="flx-col right">
                            <div className="flex-container refferals">
                                <div className="label">
                                    <label className="form-label">{cacheStore.GetString("ReferralLabel")}</label>
                                </div>
                                <div className="q">
                                    <FeatureFlagControl
                                        flag={cacheStore.ClinicSettings.EnableContactDirectory}
                                        activeFeature={(
                                            <FaxDirectorySearchComponent
                                                entityTypes={[DirectoryEntityType.HealthcareProvider]}
                                                searchText={this.state.searchReferralDoctorString}
                                                onSearchTextChanged={(_, isValid) => !isValid && this.mutateState(s => s.selectedReferalDoctor = null)}
                                                onItemSelected={item => {
                                                    this.mutateState(s => {
                                                        const doctor = DirectoryHealthCareProvider.toReferralDoctorModel(item.entity as DirectoryHealthCareProvider);

                                                        s.selectedReferalDoctor = doctor;
                                                        s.referalPhoneNumber = doctor?.Phone ?? "";
                                                        s.searchReferralDoctorString = [
                                                            !IsNullOrWhiteSpace(doctor?.LastName) ? `${doctor.LastName},` : "",
                                                            doctor?.FirstName,
                                                            !IsNullOrWhiteSpace(doctor?.LicenceNumber) ? `(${doctor.LicenceNumber})` : ""
                                                        ]
                                                            .filter(p => !IsNullOrWhiteSpace(p))
                                                            .join(" ")
                                                            .trim();
                                                    });
                                                }}
                                                clearSearchTextOnSelect={false}
                                                disableEdit
                                                isValid
                                                dataCy="contactDirectory-predefinedAppointmentReferralSearch"
                                            />
                                        )}
                                        fallbackFeature={(
                                            <DoctorSearchAutocomplete
                                                componentId="id_search_refferal"
                                                onItemSelectCallback={item => {
                                                    this.mutateState(s => {
                                                        if (item != null) {
                                                            s.selectedReferalDoctor = item;
                                                            s.referalPhoneNumber = item.Phone;
                                                        }
                                                        else {
                                                            s.selectedReferalDoctor = null;
                                                            s.referalPhoneNumber = "";
                                                        }
                                                    });
                                                }}
                                                text={this.state.searchReferralDoctorString}
                                                isValid={true}
                                            />
                                        )}
                                    />
                                </div>
                            </div>
                            <div className="flex-container refferals">
                                <div className="label">
                                    <label className="form-label">{cacheStore.GetString("PhoneLabel")}</label>
                                </div>
                                <div className="q">
                                    <input type="text" className="form-control" value={this.state.referalPhoneNumber} onChange={(e: any) => this.onReferralPhoneChanged(e.target.value)} />
                                </div>
                            </div>
                            <div className="flex-container refferals">
                                <div className="label">
                                    <label className="form-label">{cacheStore.GetString("StateLabel")}</label>
                                </div>
                                <div className="q">
                                    <select className="form-control myle-control" value={this.state.selectedAppointmentStateCode} onChange={(e: any) => this.onChangeState(e.target.value)}>
                                        {this.state.appointmentStates.map(aptState =>
                                            <option key={aptState.LookupCode} value={aptState.LookupCode}>{cacheStore.GetLocalizedValueByLookupId(aptState.LookupID)}</option>
                                        )}
                                    </select>
                                </div>
                            </div>
                            <div className="flex-container refferals">
                                <div className="label">
                                    <label className="form-label">{cacheStore.GetString("NotesDLabel")}</label>
                                </div>
                                <div className="q">
                                    <TextAreaResizable
                                        id="task_body_box"
                                        text={this.state.notes}
                                        isDisabled={false}
                                        onChange={value => this.mutateState(s => s.notes = value)}
                                        setCursorStartOnFocus={true}
                                        resetTextOnIdChange={true}
                                    />
                                </div>
                            </div>
                            {this.renderNotificationSection()}
                        </div>
                    </div>
                </div>
                <div className="ws-subfooter" style={{ marginLeft: "-20px", marginRight: "-20px", paddingLeft: "20px", paddingRight: "20px" }}>
                    <div className="flex-container content-right">
                        <div style={{ paddingRight: "5px" }}>
                            <button
                                className="btn btn-default"
                                style={{ marginRight: "5px" }}
                                onClick={() => { this.state.predefinedAppointments.forEach(item => this.clearItem(item)); this.onCancelClick(); }}
                            >
                                <span>{cacheStore.GetString("CancelLabel")}</span>
                            </button>

                            <button
                                className="btn btn-primary"
                                disabled={this.state.isSaveDisabled}
                                onClick={() => this.onSaveClick()}
                            >
                                <span>{cacheStore.GetString("SaveLabel")}</span>
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    private protectState(): void {
        if (patientStore.PatientAppointmentModels[this.props.patientContactId])
            patientStore.PatientAppointmentModels[this.props.patientContactId] = patientStore.PatientAppointmentModels[this.props.patientContactId].set("PredefinedAppointmentSaveState", this.state);
    }

    private mutatePredefinedAppointmentList(key: string, mutation: (i: PredefinedAppointmentItem) => void, then: () => void = null): void {
        this.mutateState(
            s => {
                s.predefinedAppointments = s.predefinedAppointments.map(item => {
                    if (item.key == key)
                        mutation(item);

                    return item;
                });
            },
            then
        );
    }

    private refreshNotifyPatientStatus(): void {
        const patient = patientStore.getPersonModel(this.props.patientContactId);

        const isNotifyImpossible = !patient.Patient?.Emails?.any() && !patient.Patient.getPersonalCellPhones()?.any();
        const isNotificationDisabledInServiceDefinition = this.state.predefinedAppointments.some(x => x.IsNotifyPatientDisabled);
        this.mutateState(s => {
            if (s.isTeleconsultation) {
                s.notifyPatient = true;
                s.notifyPatientDisabled = true;
                s.contactPointNotificationIds = this.getAllNotifiableContactPointIds();
            }
            else if (isNotifyImpossible) {
                s.notifyPatient = false;
                s.notifyPatientDisabled = true;
                s.contactPointNotificationIds = this.getAllNotifiableContactPointIds();
            }
            else if (isNotificationDisabledInServiceDefinition) {
                s.notifyPatient = false;
                s.notifyPatientDisabled = false;
                s.contactPointNotificationIds = cacheStore.ClinicSettings_Portal.EnablePortalNotifications && !this.hasQuestionnaire
                    ? []
                    : this.getAllNotifiableContactPointIds();
            }
            else {
                s.notifyPatient = true;
                s.notifyPatientDisabled = false;
                s.contactPointNotificationIds = this.getAllNotifiableContactPointIds();
            }

            if (s.notifyPatient)
                s.sendToAllPersonalContact = !s.contactPointNotificationIds?.any();
        });
    }

    private refreshIsTeleconsultation(): void {
        this.mutateState(
            s => s.isTeleconsultation = s.predefinedAppointments.some(x => x.IsTeleconsultation == true),
            this.refreshNotifyPatientStatus
        );
    }

    private refreshAppointmentOptions(): void {
        this.refreshNotifyPatientStatus();
        this.refreshIsTeleconsultation();
    }

    private fillEmployeesAndServicesList(appointmentList: PredefinedAppointmentItem[], employeeList: Employee[], serviceList: ServiceDefinitionModel[]): void {
        if (appointmentList != null && appointmentList.length > 0) {
            appointmentList.map(n => {
                let employee = cacheStore.GetEmployeeByContactFacetId(n.EmployeeId);
                if (employee != null)
                    employeeList.push(employee);

                if (this.state.predefinedAppointmentType === PredefinedAppointmentType.OptimizedResultAny) {
                    let possibleServices = n.AvailableServiceDefinitionIds;
                    if (serviceList.length == 0 && possibleServices != null && possibleServices.length > 0) {
                        possibleServices.map(s => serviceList.push(cacheStore.GetServiceDefinitionById(s)));
                    }
                }
                else if (this.state.predefinedAppointmentType === PredefinedAppointmentType.PredefinedAppointment) {
                    var serviceDef = employee.ServicesOffered.map(s => s.ServiceDefinitionModel);
                    serviceDef.forEach(sd => serviceList.push(sd));
                }
                else {
                    if (!serviceList.map(x => x.Model.Id).some(z => z == n.ServiceDefinitionId)) {
                        var service = cacheStore.GetServiceDefinitionById(n.ServiceDefinitionId);
                        serviceList.push(service);
                    }
                }
            });
        }
    }

    private clearItem(predefAppt: PredefinedAppointmentItem): void {
        this.mutatePredefinedAppointmentList(predefAppt.key, i => {
            i.IsInEditMode = false;
        });
    }

    private changeItem(predefAppt: PredefinedAppointmentItem): void {
        const self = this;
        self.mutatePredefinedAppointmentList(predefAppt.key, i => {
            i.DefaultName = i.Name;
            i.DefaultServiceDefinitionId = i.ServiceDefinitionId;
            i.IsInEditMode = false;
        });
    }

    private onDispatchEvent(event: LocalEvent): void {
        if (event.Operation === LocalOperations.Misc.ExternallyCalled_SavePatientAppointmentSearch) {
            const forceSave = event.Get<boolean>("force");
            this.mutateState(
                s => s.isSaveDisabled = true,
                () => this.saveAppointment(forceSave)
            );
        }
    }

    private onSaveClick(forceSave: boolean = false): void {
        this.mutateState(
            s => s.isSaveDisabled = true,
            () => this.checkContactPointsAndSave(forceSave)
        );
    }

    private async checkContactPointsAndSave(forceSave: boolean = false): Promise<void> {
        try {
            if (this.hasQuestionnaire && this.isPatientAnAdult() && this.hasNoValidContactPoints()) {
                this.mutateState(s => {
                    s.isSaveDisabled = true;
                    s.showAddContactPointWarning = true;
                });
                return;
            }

            if (this.hasQuestionnaire && !this.isPatientAnAdult()) {
                const patientRepresentantValidator = await PatientHelper.getPatientEmailValidator(this.props.patientContactId);

                if (!patientRepresentantValidator.PatientHasRepresentantWithValidContact)
                    await this.openAddEmailPopup(patientRepresentantValidator, this.hasQuestionnaire);
            }

            else if (cacheStore.ClinicSettings.EnableTelemedFeature
                && cacheStore.ClinicSettings.EnableIsTeleconsultationCheckbox
                && this.state.isTeleconsultation
            )
                await PatientHelper.ensurePatientHasValidEmail(
                    this.props.patientContactId,
                    async patientEmailValidator => this.openAddEmailPopup(patientEmailValidator)
                );

            this.saveAppointment(forceSave);
        }
        catch (e) {
            console.error(e);
            this.mutateState(s => s.isSaveDisabled = false);
        }
    }

    private isPatientAnAdult(): boolean {
        const patient = patientStore.getCurrentPatient();

        return (
            PatientHelper.getPatientIsOfAgeOfConsent(patient.IndividualResponse?.DateOfBirth)
            || !patient.IndividualResponse?.DateOfBirth
        );
    }

    private hasNoValidContactPoints(): boolean {
        let patient = patientStore.getCurrentPatient();

        return (isArrayNullOrUndefinedOrEmpty(patient.Emails)
            && (!cacheStore.portalConfiguration?.HasSmsNotificationEnabled || isArrayNullOrUndefinedOrEmpty(patient.Phones)));
    }

    private async openAddEmailPopup(patientEmailValidator: PatientEmailValidator, hasQuestionnaire: boolean = false): Promise<void> {
        return new Promise((resolve, reject) => {
            this.mutateState(s => {
                s.addEmailPopupProps = this.initAddEmailPopupControlProps(
                    () => resolve(),
                    () => reject("Adding new email cancelled."),
                    patientEmailValidator,
                    hasQuestionnaire
                );
                s.showAddEmailPopup = true;
            });
        });
    }

    private initAddEmailPopupControlProps(
        onSubmit: () => void,
        onCancel: () => void,
        patientEmailValidator: PatientEmailValidator,
        hasQuestionnaire: boolean
    ): AddRepresentativeEmailPopupProps {
        let patientUnderAgeWithoutRepresentantLabel: string = hasQuestionnaire
            ? "QuestionnaireRequiresRepresentativeLabel"
            : "TeleconsultationRequresEmailAndPatientUnderAgeOfConsentWithoutRepresentativeLabel";

        let patientWithoutRepresentantContactLabel: string = hasQuestionnaire
            ? "QuestionnaireRequiresRepresentativeContactLabel"
            : "TeleconsultationRequresEmailAndPatientUnderAgeOfConsentLabel";

        return {
            ...AddRepresentativeEmailPopupProps.defaultProps,
            representantsDto: patientEmailValidator.Representants,

            enablePhoneContent: hasQuestionnaire,

            patientUnderAgeWithoutRepresentantLabel: patientUnderAgeWithoutRepresentantLabel,

            patientWithoutRepresentantContactLabel: patientWithoutRepresentantContactLabel,

            onCancel: () => {
                this.mutateState(
                    s => {
                        s.showAddEmailPopup = false;
                        s.isSaveDisabled = false;
                    },
                    () => onCancel()
                );
            },

            onSubmit: () => {
                this.mutateState(
                    s => s.showAddEmailPopup = false,
                    () => onSubmit()
                );
            },

            onContinueWithoutContact: () => {
                this.mutateState(
                    s => s.showAddEmailPopup = false,
                    () => onSubmit()
                );
            }

        };
    }

    private saveAppointment(forceSave: boolean = false): void {
        const patient = patientStore.getCurrentPatient();
        const state = new PredefinedAppointmentSaveState(this.state);

        if (!state.contactPointNotificationIds?.any() && state.sendToAllPersonalContact)
            state.contactPointNotificationIds = patient.getAllPersonalContactPointIds();

        if (this.state.predefinedAppointmentType == PredefinedAppointmentType.OptimizedResult)
            patientStore.ActionsAppointment.PortalAppointmentSave(state, this.props.patientContactId, forceSave);
        else
            patientStore.ActionsAppointment.SavePredefinedAppointment(state, this.props.patientContactId, forceSave);
    }

    private onReferralPhoneChanged(value: string): void {
        this.mutateState(s => s.referalPhoneNumber = value);
    }

    private onCancelClick(): void {
        sessionStore.dispatch(LocalPatientOperations.Appointment.CancelSavePredefinedAppointment);
    }

    private onChangeState(value: string): void {
        this.mutateState(s => s.selectedAppointmentStateCode = value);
    }

    private renderNotificationSection(): JSX.Element {
        if (!cacheStore.ClinicSettings_Portal.EnablePortalNotifications && !this.hasQuestionnaire)
            return null;

        return (
            <div className="flex-container refferals">
                <div className="label">
                    <label className="form-label" />
                </div>
                <div className="q">
                    <PatientContactDisplay
                        patient={patientStore.getCurrentPatient()}
                        showNotificationCheckbox
                        shouldRenderAllContactTypes
                        showOnlyCellPhonesAndEmails={false}
                        sendToAllPersonalContact={this.state.sendToAllPersonalContact}
                        onSendToAllPersonalContactChange={checked => this.mutateState(s => s.sendToAllPersonalContact = checked)}
                        contactPointNotificationIds={this.state.contactPointNotificationIds}
                        hasEmailNotificationsEnabled={this.hasQuebecBookingHubEnabled || cacheStore.portalConfiguration?.HasEmailNotificationEnabled}
                        hasSmsNotificationsEnabled={this.hasQuebecBookingHubEnabled || cacheStore.portalConfiguration?.HasSmsNotificationEnabled}
                        isNotificationSet={this.state.notifyPatient || this.hasQuestionnaire}
                        onPhoneNotificationChange={(checked, phone) => this.onContactPointCheck(checked, phone.ContactPoint_id)}
                        onEmailNotificationChange={(checked, email) => this.onContactPointCheck(checked, email.ContactPoint_id)}
                        onRepresentativePhoneNotificationChange={(checked, phone) => this.onContactPointCheck(checked, phone.ContactPoint_id)}
                        onRepresentativeEmailNotificationChange={(checked, email) => this.onContactPointCheck(checked, email.ContactPoint_id)}
                    />
                </div>
            </div>
        );
    }

    private onNotifyPatientCheck(checked: boolean): void {
        let contactPointIds = [];

        if (checked)
            contactPointIds = this.getAllNotifiableContactPointIds();

        this.mutateState(s => {
            if (!this.hasQuestionnaire || checked)
                s.contactPointNotificationIds = contactPointIds;
            s.notifyPatient = checked;
        });
    }

    private onContactPointCheck(checked: boolean, contactPointId: string): void {
        let contactPointIds = this.state.contactPointNotificationIds || [];

        checked
            ? contactPointIds.push(contactPointId)
            : contactPointIds = contactPointIds.filter(x => x != contactPointId);

        this.mutateState(
            s => {
                s.contactPointNotificationIds = contactPointIds;
                s.sendToAllPersonalContact = !contactPointIds?.any();
            },
            () => this.checkNotifyPatientIfAtLeastOneContactPointChecked()
        );
    }

    private checkNotifyPatientIfAtLeastOneContactPointChecked(): void {
        if (!this.hasQuestionnaire && this.state.contactPointNotificationIds?.any())
            this.mutateState(s => s.notifyPatient = true);
    }

    private getAllNotifiableContactPointIds(): string[] {
        let contactPointIds = [];
        let patient = patientStore.getCurrentPatient();
        let cellType = cacheStore.FindLookupByLookupCode("MOBILE", "PHONETYPE");

        const addToContactPointIds = <T extends INotificationContactPoint>(contacts: T[], condition?: (contact: T) => boolean): void => {
            for (let contact of (contacts ?? [])) {
                if ((condition?.(contact) ?? true) && contact.NotificationsEnabled)
                    contactPointIds.push(contact.ContactPoint_id);
            }
        };

        addToContactPointIds(patient.Emails);
        addToContactPointIds(patient.Phones, phone => phone.PhoneTypeLookupId == cellType.LookupID);

        for (let relationship of patient.Relationships?.filter(p => p.IsRepresentative) ?? []) {
            addToContactPointIds(relationship.Emails);
            addToContactPointIds(relationship.PhoneNumbers, phone => phone.PhoneTypeLookupId == cellType.LookupID);
        }
        return contactPointIds;
    }

    private get hasQuestionnaire(): boolean {
        return cacheStore.ClinicSettings.EnableCovidQuestionnaire && this.state.predefinedAppointments.some(x => x.HasQuestionnaire);
    }

    private get hasQuebecBookingHubEnabled(): boolean {
        return this.state.predefinedAppointments.some(predefinedAppointment => {
            const site = cacheStore.GetSiteById(predefinedAppointment.SiteId);
            const employee = cacheStore.GetEmployeeByContactFacetId(predefinedAppointment.EmployeeId);

            return site?.Model.BookingHubEnabled && employee?.BookingHubEnabled;
        });
    }
}

export { PredefinedAppointmentSave, PredefinedAppointmentSaveState, PredefinedAppointmentType };
